import { useMemo } from 'react';
import { WindowFrame } from '../../../components/window';
import type { WindowPanelProps } from '../../../types/window';
import { SHIP_OPTIONS, type ShipId } from '../../../game/catalog';
import { ShipGarageStage } from './ShipGarageStage';
import './styles.css';

export interface ShipsPickerProps extends WindowPanelProps {
  /** Currently equipped craft (next flight). */
  selectedId: ShipId;
  /** Browse focus in the carousel (may differ until USE CRAFT). */
  browseId: ShipId;
  onBrowse: (id: ShipId) => void;
  onConfirm: () => void;
}

function shipIndex(id: ShipId): number {
  const i = SHIP_OPTIONS.findIndex((s) => s.id === id);
  return i >= 0 ? i : 0;
}

const ShipsPicker = (props: ShipsPickerProps) => {
  const { selectedId, browseId, onBrowse, onConfirm } = props;
  const index = shipIndex(browseId);
  const n = SHIP_OPTIONS.length;
  const current = SHIP_OPTIONS[index] ?? SHIP_OPTIONS[0];
  const prev = SHIP_OPTIONS[(index - 1 + n) % n];
  const next = SHIP_OPTIONS[(index + 1) % n];
  const isEquipped = browseId === selectedId;

  const go = (delta: number) => {
    const nextIndex = (index + delta + n) % n;
    const ship = SHIP_OPTIONS[nextIndex];
    if (ship) {
      onBrowse(ship.id);
    }
  };

  const dots = useMemo(
    () =>
      SHIP_OPTIONS.map((ship, i) => (
        <button
          key={ship.id}
          type="button"
          className={i === index ? 'ship-garage__dot ship-garage__dot--on' : 'ship-garage__dot'}
          aria-label={`Show ${ship.name}`}
          onClick={() => onBrowse(ship.id)}
        />
      )),
    [index, onBrowse],
  );

  return (
    <WindowFrame
      title="SPACESHIP"
      windowId="ships"
      zIndex={props.data}
      theme="dark"
      sizePreset="lg"
      onClose={() => props.parentCallback(false, 'showHideShips')}
      onFocus={() => props.parentCallbackTwo(0, 0, 0, 0, 0, 0, 1)}
      contentClassName="ship-garage-window-content"
    >
      <div className="ship-garage">
        <div className="ship-garage__stage-wrap">
          <ShipGarageStage shipId={browseId} className="ship-garage__stage" />

          {prev ? (
            <div className="ship-garage__ghost ship-garage__ghost--left" aria-hidden>
              <span className="ship-garage__ghost-name">{prev.name}</span>
              <span className="ship-garage__ghost-tag">{prev.tags[0] ?? ''}</span>
            </div>
          ) : null}
          {next ? (
            <div className="ship-garage__ghost ship-garage__ghost--right" aria-hidden>
              <span className="ship-garage__ghost-name">{next.name}</span>
              <span className="ship-garage__ghost-tag">{next.tags[0] ?? ''}</span>
            </div>
          ) : null}

          <button
            type="button"
            className="ship-garage__arrow ship-garage__arrow--left"
            aria-label="Previous spaceship"
            onClick={() => go(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="ship-garage__arrow ship-garage__arrow--right"
            aria-label="Next spaceship"
            onClick={() => go(1)}
          >
            ›
          </button>

          <p className="ship-garage__hint">DRAG TO ORBIT · ARROWS TO SWITCH</p>
        </div>

        <div className="ship-garage__info">
          <div className="ship-garage__title-row">
            <h2 className="ship-garage__title">{current?.name ?? 'Craft'}</h2>
            {isEquipped ? <span className="ship-garage__active-badge">ACTIVE</span> : null}
          </div>
          <p className="ship-garage__desc">{current?.description}</p>
          <div className="ship-garage__tags">
            {(current?.tags ?? []).map((tag) => (
              <span key={tag} className="ship-garage__tag">
                {tag}
              </span>
            ))}
          </div>
          <div className="ship-garage__actions">
            <button
              type="button"
              className="ship-garage__use-btn"
              disabled={isEquipped}
              onClick={onConfirm}
            >
              {isEquipped ? 'EQUIPPED' : 'USE CRAFT'}
            </button>
            <div className="ship-garage__dots" aria-hidden>
              {dots}
            </div>
          </div>
        </div>
      </div>
    </WindowFrame>
  );
};

export default ShipsPicker;
